import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiEdit, FiShield, FiUsers, FiArrowRight, FiCheckCircle, FiClipboard } = FiIcons;

function Quiz({ setQuizResult, quizResult }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);

  const questions = [
    {
      id: 1,
      question: "What's your biggest challenge right now?",
      options: [
        { text: "I'm not sure if I'm called to write", persona: "christian" },
        { text: "I have ideas but don't know how to start writing", persona: "creator" },
        { text: "I have a manuscript but don't know how to publish", persona: "caretaker" },
        { text: "My book is done but I don't know how to share it", persona: "connector" }
      ]
    },
    {
      id: 2,
      question: "Which statement resonates most with you?",
      options: [
        { text: "I need clarity on God's calling for my writing", persona: "christian" },
        { text: "I need a plan to turn my ideas into a book", persona: "creator" },
        { text: "I need guidance on the publishing process", persona: "caretaker" },
        { text: "I need help connecting with readers", persona: "connector" }
      ]
    },
    {
      id: 3,
      question: "Where are you in your writing journey?",
      options: [
        { text: "Just feeling the pull to write", persona: "christian" },
        { text: "Ready to start writing but need structure", persona: "creator" },
        { text: "Have a completed or nearly completed manuscript", persona: "caretaker" },
        { text: "Published or ready to publish", persona: "connector" }
      ]
    },
    {
      id: 4,
      question: "What would feel like the biggest win right now?",
      options: [
        { text: "Confidence that God has called me to write", persona: "christian" },
        { text: "A clear outline and writing schedule", persona: "creator" },
        { text: "Understanding my publishing options", persona: "caretaker" },
        { text: "Finding my first readers", persona: "connector" }
      ]
    }
  ];

  const personaInfo = {
    christian: {
      title: 'Christian',
      subtitle: 'Mindset & Motivation',
      icon: FiHeart,
      color: 'rose',
      description: 'Start with your foundation—understanding your calling and identity as a Christian author.',
      nextStep: 'Explore your calling and identity as a Christian author.',
      showChecklist: false
    },
    creator: {
      title: 'Creator',
      subtitle: 'Book Planning & Writing',
      icon: FiEdit,
      color: 'amber',
      description: 'Focus on planning your book and developing a sustainable writing practice.',
      nextStep: 'Create a concrete plan for your book with our interactive checklist.',
      showChecklist: true
    },
    caretaker: {
      title: 'Caretaker',
      subtitle: 'Publishing Roadmap',
      icon: FiShield,
      color: 'emerald',
      description: 'Navigate the publishing process with wisdom and confidence.',
      nextStep: 'Learn about your publishing options and next steps.',
      showChecklist: false
    },
    connector: {
      title: 'Connector',
      subtitle: 'Marketing Mindset & First Steps',
      icon: FiUsers,
      color: 'blue',
      description: 'Learn to share your work with a heart to serve your readers.',
      nextStep: 'Discover gospel-centered marketing approaches.',
      showChecklist: false
    }
  };

  const handleAnswer = (answer) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate result
      const personaCounts = {};
      newAnswers.forEach(answer => {
        personaCounts[answer.persona] = (personaCounts[answer.persona] || 0) + 1;
      });

      const result = Object.keys(personaCounts).reduce((a, b) => 
        personaCounts[a] > personaCounts[b] ? a : b
      );

      setQuizResult(result);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setQuizResult(null);
  };

  if (quizResult) {
    const persona = personaInfo[quizResult];
    const colorClasses = {
      rose: 'from-rose-50 to-pink-50 border-rose-200 text-rose-700 bg-rose-600 hover:bg-rose-700',
      amber: 'from-amber-50 to-yellow-50 border-amber-200 text-amber-700 bg-amber-600 hover:bg-amber-700',
      emerald: 'from-emerald-50 to-green-50 border-emerald-200 text-emerald-700 bg-emerald-600 hover:bg-emerald-700',
      blue: 'from-blue-50 to-indigo-50 border-blue-200 text-blue-700 bg-blue-600 hover:bg-blue-700'
    };

    const colors = colorClasses[persona.color].split(' ');

    return (
      <div className={`min-h-screen bg-gradient-to-br ${colors[0]} ${colors[1]} flex items-center justify-center px-4`}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-serif text-4xl text-gray-800 mb-4">
              Your Focus Area
            </h1>
          </div>

          <div className={`bg-white/80 backdrop-blur-sm rounded-2xl p-8 border ${colors[2]} mb-8`}>
            <div className="mb-6">
              <div className="inline-flex p-4 bg-white rounded-2xl shadow-sm mb-4">
                <SafeIcon icon={persona.icon} className={`w-12 h-12 ${colors[3]}`} />
              </div>
              <h2 className="font-serif text-3xl text-gray-800 mb-2">
                The {persona.title}
              </h2>
              <p className={`text-xl font-medium ${colors[3]} mb-4`}>
                {persona.subtitle}
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {persona.description}
              </p>
              <p className="text-gray-600 text-base">
                <strong>Recommended next step:</strong> {persona.nextStep}
              </p>
            </div>

            <div className="space-y-4">
              {/* Primary Action - Show Checklist for Creator, Persona for others */}
              {persona.showChecklist ? (
                <Link 
                  to="/checklist"
                  className={`block w-full ${colors[4]} ${colors[5]} text-white py-4 px-6 rounded-xl transition-colors`}
                >
                  <div className="flex items-center justify-center space-x-3">
                    <SafeIcon icon={FiClipboard} className="w-5 h-5" />
                    <span className="text-lg font-medium">Start Your Book Plan</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </div>
                </Link>
              ) : (
                <Link 
                  to={`/persona/${quizResult}`}
                  className={`block w-full ${colors[4]} ${colors[5]} text-white py-4 px-6 rounded-xl transition-colors`}
                >
                  <div className="flex items-center justify-center space-x-3">
                    <span className="text-lg font-medium">Explore This Persona</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </div>
                </Link>
              )}

              {/* Secondary Action */}
              <div className="flex space-x-3">
                {persona.showChecklist && (
                  <Link 
                    to={`/persona/${quizResult}`}
                    className="flex-1 bg-white text-gray-700 border border-gray-300 py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors text-center"
                  >
                    Learn More About This Role
                  </Link>
                )}
                <button 
                  onClick={resetQuiz}
                  className="flex-1 text-gray-600 hover:text-gray-800 py-3 transition-colors text-center"
                >
                  Retake Quiz
                </button>
              </div>
            </div>
          </div>

          <p className="text-gray-600 italic">
            "You don't need to hustle—just take the next faithful step."
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-emerald-50 flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-amber-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Question */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-100">
          <h2 className="font-serif text-2xl text-gray-800 mb-8 text-center">
            {questions[currentQuestion].question}
          </h2>

          <div className="space-y-4">
            {questions[currentQuestion].options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                className="w-full text-left p-4 bg-white rounded-xl border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all duration-200 transform hover:scale-105"
              >
                <p className="text-gray-700">{option.text}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Encouraging Message */}
        <div className="text-center mt-8">
          <p className="text-gray-600 italic">
            "Let's walk this road together."
          </p>
        </div>
      </div>
    </div>
  );
}

export default Quiz;